import { ActionButton } from '@emerald-works-nova/components'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { Box, Collapse, Divider, Grid, List, ListItem, ListItemText, Switch, Typography, useTheme } from '@mui/material'
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { BannerStatic } from '../../../components'
import { TenantEditSlice } from '../../../reducers'
import { useStyles } from './style'
import { useFeatureFormContext } from './feature-form-context'

const PlaylistsSettings = () => {
  const classes = useStyles()
  const { spacing } = useTheme()
  const dispatch = useDispatch()
  const { tenant } = useFeatureFormContext()
  const [isLoading, setIsLoading] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(false)

  // PENG-3783: features.playlists (Boolean) became features.playlist (Object). The legacy
  // fallback covers tenants the CHANGE_PLAYLISTS_STRUCTURE patch has not reached yet; drop it
  // once every environment is confirmed migrated.
  const [value, setValue] = useState({
    enabled: tenant?.features?.playlist?.enabled ?? tenant?.features?.playlists ?? false,
    adminOnlyThumbnailUpload: tenant?.features?.playlist?.adminOnlyThumbnailUpload ?? false
  })

  const handleSwitch = () => {
    setValue(current => ({ ...current, enabled: !current.enabled }))
  }

  const handleAdminOnlyThumbnailSwitch = () => {
    setValue(current => ({
      ...current,
      adminOnlyThumbnailUpload: !current.adminOnlyThumbnailUpload
    }))
  }

  const handlePlaylistsSettings = () => setIsCollapsed(collapsed => !collapsed)

  const [tenantUpdate] = useEvent([
    {
      eventName: 'tenantUpdate',
      onSuccess: (tenant) => {
        BannerStatic.show({ label: 'Playlists configuration updated' })
        setIsLoading(false)
        dispatch(TenantEditSlice.actions.updateTenantFeatures(tenant.features))
      },
      onError: () => {
        BannerStatic.show({
          label: 'There was an error saving the Playlists feature configuration',
          isError: true
        })
        setIsLoading(false)
      }
    }
  ])

  const handleSave = () => {
    // The legacy `playlists` key is removed fleet-wide by CHANGE_PLAYLISTS_STRUCTURE, not here.
    tenantUpdate.trigger({
      pk: tenant.pk,
      features: {
        ...tenant.features,
        playlist: value
      }
    })
    setIsLoading(true)
  }

  return (
    <Grid container className={classes.featContainer}>
      <Grid item xs={12}>
        <Box
          border={1}
          borderRadius='undefinedpx'
          borderColor='#E0E0E0'
          display='flex'
          style={{
            justifyContent: 'space-between',
            flexDirection: 'column',
            padding: spacing(1, 4)
          }}
        >
          <Box display='flex' justifyContent='space-between' alignItems='flex-start'>
            <Typography variant='body2' sx={{ mt: spacing(3.5) }}>
              Playlists
            </Typography>
            <Box display='flex' justifyContent='flex-end' alignItems='center' padding={spacing(1)}>
              {value.enabled && !isLoading && (
                <>
                  <Box sx={{ ':hover': { cursor: 'pointer' } }}>
                    <List component='nav' aria-label='Playlists settings'>
                      <ListItem
                        onClick={handlePlaylistsSettings}
                        aria-haspopup='true'
                        aria-controls='playlists-settings-menu'
                        aria-label='Playlists Settings'
                      >
                        <ListItemText disableTypography sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant='body2'>Playlists Settings</Typography>
                          {isCollapsed ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </ListItemText>
                      </ListItem>
                    </List>
                  </Box>
                  <Divider variant='middle' flexItem orientation='vertical' sx={{ mx: spacing(2), borderColor: 'rgba(0, 0, 0, 0.38)' }} />
                </>
              )}
              <Switch
                color='primary'
                data-test='playlist-feature-toggle'
                checked={value.enabled}
                onChange={handleSwitch}
                disabled={isLoading}
              />
            </Box>
          </Box>

          <Collapse in={isCollapsed}>
            {value.enabled && !isLoading && (
              <Box display='flex' flexDirection='column' gap={2} mb={spacing(1)}>
                <Box display='flex' justifyContent='space-between' alignItems='center'>
                  <Box width='50%'>
                    <Typography variant='body1' sx={{ mt: spacing(1), fontWeight: 500 }}>
                      Admin Only Thumbnail Upload
                    </Typography>
                    <Typography variant='body2' sx={{ mt: spacing(1) }}>
                      When on, only administrators and the Client Experience team can upload playlist thumbnails. Other users' playlists use a thumbnail derived from the content they add.
                    </Typography>
                  </Box>
                  <Switch
                    color='primary'
                    checked={value.adminOnlyThumbnailUpload}
                    onChange={handleAdminOnlyThumbnailSwitch}
                    disabled={!value.enabled}
                    data-test='playlist-admin-only-thumbnail-toggle'
                  />
                </Box>
              </Box>
            )}
          </Collapse>

          <Divider style={{ marginBottom: spacing(1) }} />

          <Box display='flex' justifyContent='flex-end' mt={1} mb={1}>
            <ActionButton
              onClick={handleSave}
              disabled={isLoading}
              variant='primary'
              dataTest='playlists-save-button'
              style={{ height: 'min-content' }}
            > Save
            </ActionButton>
          </Box>

        </Box>

      </Grid>

    </Grid>
  )
}

export default PlaylistsSettings
